import { useEffect, useState } from "react";
import { AuthShell, CardThemeToggle } from "./AuthShell";
import { apiUrl, clearCachedIdentity, clearTokens, setTokens } from "../../utils/jwt-storage";
import { refreshAuthHeaders } from "../../providers/ApiProvider";
import { spaNavigate } from "../../utils/navigation";

/**
 * Where a sign-in that happened elsewhere lands (a provider's button, an
 * organisation's own sign-in): ``?code=`` is exchanged for this app's
 * tokens and a session, both for the person the code is for. Whatever the
 * browser held is dropped first: it was whoever was signed in before.
 */
export const Continue = () => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let code = "";
    try {
      code = new URLSearchParams(window.location.search).get("code") || "";
    } catch {
      /* no address to read */
    }
    clearCachedIdentity();
    clearTokens();
    refreshAuthHeaders();
    try {
      window.sessionStorage.removeItem("afri:login:auto-home");
      // The code is for one use: keep it out of the history.
      window.history.replaceState({}, "", "/auth/continue");
    } catch {
      /* nothing to tidy */
    }
    if (!code) {
      setError("This sign-in has expired. Start again from the sign-in page.");
      return;
    }
    let alive = true;
    (async () => {
      try {
        const res = await fetch(apiUrl("/api/auth/exchange/"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ code }),
        });
        const body = (await res.json().catch(() => ({}))) as {
          access?: string;
          refresh?: string;
          error?: string;
          user?: { id?: number };
        };
        if (!alive) return;
        if (!res.ok || !body.access) {
          setError(body.error || "This sign-in did not go through.");
          return;
        }
        setTokens(body.access, body.refresh ?? null);
        refreshAuthHeaders();
        if (body.user?.id != null) {
          try {
            window.localStorage.setItem("afri:last-user", JSON.stringify(body.user));
          } catch {
            /* the shell fetches it instead */
          }
        }
        // A full load: the whole app starts as this person, with nothing of the previous one.
        window.location.replace("/");
      } catch {
        if (alive) setError("The server could not be reached. Try again.");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <AuthShell>
      <div className="form-wrapper">
        <div className="form-wrapper-header">
          <h2>{error ? "Sign-in did not go through" : "Signing you in"}</h2>
          <CardThemeToggle />
        </div>
        {error ? (
          <>
            <p className="auth-said">{error}</p>
            <button type="button" className="login-button" onClick={() => spaNavigate("/auth/login", true)}>
              Back to sign in
            </button>
          </>
        ) : (
          <p className="auth-said auth-said_quiet">One moment.</p>
        )}
      </div>
    </AuthShell>
  );
};

export default Continue;
